import React, { FC, ReactNode, useCallback } from 'react';
import { FormGroup, FormText, Input, InputGroup, InputGroupText, Label } from 'reactstrap';
import { getDate } from '../../../components/Date';
import { TranslationKey, useTranslation } from '../../../translations';
import { useConfig } from '../../../utils';

interface ConfigSettingProps<T> {
  readonly name: string;
  readonly displayName: TranslationKey;
  readonly disabled?: boolean;
  readonly description: TranslationKey | '';
  readonly saving: boolean;
  readonly value: T | null;
  readonly update: (value: T) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  readonly placeholder?: string;
}

export const ConfigBooleanSetting: FC<ConfigSettingProps<boolean>> = ({
  displayName,
  disabled,
  description,
  name,
  value,
  saving,
  update,
}) => {
  const { ts } = useTranslation();

  return (
    <FormGroup check className="start-align mt-10">
      <Input
        checked={!!value}
        disabled={disabled || saving}
        id={name}
        onChange={(e) => {
          update(e.target.checked);
        }}
        type="checkbox"
      />
      <Label check for={name}>
        {ts(displayName)}
      </Label>
      <FormText color="muted" style={{ display: 'inline-block', marginLeft: '4px' }}>
        {description === '' ? '' : ts(description)}
      </FormText>
    </FormGroup>
  );
};

interface ConfigTextBoxSettingProps<T extends number | string | null>
  extends ConfigSettingProps<T> {
  readonly allowNull?: boolean;
}

interface BaseConfigTextBoxSettingProps<T extends number | string | null>
  extends ConfigTextBoxSettingProps<T> {
  readonly children: ReactNode;
  readonly defaultValue: T;
}

const BaseConfigTextBoxSetting = <T extends number | string>(
  props: BaseConfigTextBoxSettingProps<T | null>,
) => {
  const {
    displayName,
    description,
    name,
    value,
    saving,
    update,
    allowNull,
    children,
    defaultValue,
    disabled,
  } = props;

  const { ts } = useTranslation();

  return (
    <div className={`start-align mt-10${allowNull ? ' form-check' : ''}`}>
      <Label check={allowNull} for={`${name}-checkbox`}>
        {ts(displayName)}
      </Label>
      {allowNull && (
        <Input
          checked={value !== null}
          disabled={saving || disabled}
          id={`${name}-checkbox`}
          onChange={(e) => {
            update(e.target.checked ? defaultValue : null);
          }}
          style={{ marginTop: '-21px' }}
          type="checkbox"
        />
      )}
      {children}
      <FormText color="muted">{description === '' ? '' : ts(description)}</FormText>
    </div>
  );
};

export const ConfigTextBoxSetting: FC<ConfigTextBoxSettingProps<string | null>> = (props) => {
  const { name, value, saving, update, placeholder, disabled } = props;

  return (
    <BaseConfigTextBoxSetting {...props} defaultValue="">
      <Input
        disabled={value === null || saving || disabled}
        id={name}
        name={name}
        onChange={(e) => {
          update(e.target.value);
        }}
        placeholder={placeholder}
        value={value?.toString()}
      />
    </BaseConfigTextBoxSetting>
  );
};

interface ConfigNumberBoxSettingProps extends ConfigTextBoxSettingProps<number | null> {
  readonly currency?: boolean;
}

export const ConfigNumberBoxSetting: FC<ConfigNumberBoxSettingProps> = (props) => {
  const { currencySymbol } = useConfig();
  const { name, value, saving, update, placeholder, currency, disabled } = props;
  return (
    <BaseConfigTextBoxSetting {...props} defaultValue={0}>
      <InputGroup>
        {currency && <InputGroupText>{currencySymbol}</InputGroupText>}
        <Input
          disabled={value === null || saving || disabled}
          id={name}
          name={name}
          onChange={(e) => {
            update(e.target.valueAsNumber);
          }}
          placeholder={placeholder}
          type="number"
          value={value?.toString() ?? '0'}
        />
      </InputGroup>
    </BaseConfigTextBoxSetting>
  );
};

interface ConfigDateSettingProps extends ConfigSettingProps<Date> {
  readonly timezone: string;
}

export const ConfigDateSetting: FC<ConfigDateSettingProps> = ({
  value,
  timezone,
  update,
  name,
  displayName,
  description,
  saving,
  disabled,
}) => {
  const parseDate = useCallback(
    (input: string) => {
      const before = new Date(input);
      const mid = changeTimezone(before, 'UTC');
      const newDate = changeTimezone(mid, timezone, true);
      if (!Number.isNaN(newDate.getTime())) {
        update(newDate);
      }
    },
    [timezone, update],
  );

  const { ts } = useTranslation();

  return (
    <div className="start-align mt-10">
      <FormGroup>
        <Label for={name}>{ts(displayName)}</Label>
        <Input
          defaultValue={getDate(value!)}
          disabled={saving || disabled}
          id={name}
          name={name}
          onChange={(e) => {
            parseDate(e.target.value);
          }}
          type="date"
        />
        <FormText color="muted">{description === '' ? '' : ts(description)}</FormText>
      </FormGroup>
    </div>
  );
};

function changeTimezone(date: Date, ianatz?: string, inverse = false): Date {
  // Taken from https://stackoverflow.com/a/53652131/99966
  const invdate = new Date(
    date.toLocaleString('en-US', {
      timeZone: ianatz,
    }),
  );

  const diff = date.getTime() - invdate.getTime();
  return new Date(date.getTime() - (inverse ? -diff : diff));
}
